import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { StorageService } from '@utils/storage';

/**
 * Axios instance configured for session-based authentication
 * Matches Angular's subscriber.interceptor.ts and error handling
 */
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Always include cookies for session auth
    // Spring's Pageable resolver expects repeated `sort=a&sort=b` rather than
    // Axios's default `sort[]=a&sort[]=b` for array params. `indexes: null`
    // drops the brackets so multi-sort and any other array param hits Spring
    // correctly.
    paramsSerializer: { indexes: null },
});

/**
 * Request interceptor to add subscriberId header
 * Matches Angular's subscriber.interceptor.ts
 */
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Add subscriberId header if available
        const subscriberId = StorageService.getSubscriberId();
        if (subscriberId && config.headers) {
            config.headers['subscriberId'] = subscriberId.toString();
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor for error handling
 * Matches Angular's error handling pattern
 */
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const { config, response } = error;

        // Handle 401 Unauthorized
        if (response?.status === 401) {
            // Don't redirect for /account endpoint (used to check auth status)
            const isAccountEndpoint = config?.url?.includes('/account');

            if (!isAccountEndpoint) {
                // Store current URL for redirect after login
                const currentPath = window.location.hash.replace('#', '') || window.location.pathname;
                if (currentPath && currentPath !== '/' && currentPath !== '/login') {
                    sessionStorage.setItem('redirectUrl', currentPath);
                }

                // Clear auth state and redirect to home
                StorageService.clearAll();

                // Use hash routing (matches Angular's useHash: true)
                window.location.href = '/#/';
            }
        }

        return Promise.reject(error);
    }
);

export default api;
